import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useContext, useEffect, useState } from 'react';
import '../../App.css';
import { StyleConstants, Styles } from '../../Styles';
import FaultTable from '../../components/FaultTable';
import LightStatusStack from '../../components/LightStatusStack';
import { API_CALL_INTERVAL } from '../../config/Config';
import RestfulManager, { RES_STATUS_OK } from '../../manager/RestfulManager';
import SnackbarContext from '../../components/SnackbarContext';
import LrvStatusTable from '../../components/LrvStatusTable';
import RouteCanvas from '../../components/RouteCanvas';
import SessionManager from '../../manager/SessionManager';
import useInterval from '../../hook/useInterval';

const HomePage = () => {
    const { setOpenSnackBar, setSnackbarSeverity, setSnackbarMessage } = useContext(SnackbarContext);
    
    const sessionManager = new SessionManager();

    const restfulManager = new RestfulManager();

    const [stations, setStations] = useState([]);
    const [routeStations, setRouteStations] = useState([]);

    const [carPoss, setCarPoss] = useState([]);
    const [faults, setFaults] = useState([]);
    const [carFaults, setCarFaults] = useState([]);

    const [onlineCarCount, setOnlineCarCount] = useState(0);
    const [offlineCarCount, setOfflineCarCount] = useState(0);
    const [carCount, setCarCount] = useState(0);

    const [faultCount, setFaultCount] = useState(0);
    const [levelOneFaultCount, setLevelOneFaultCount] = useState(0);
    const [levelTwoFaultCount, setLevelTwoFaultCount] = useState(0);

    const getLatestCarPoss = async (carCount) => {
        try {
            const res = await restfulManager.getLatestCarPoss(sessionManager.getAccessToken());
            const json = await res.json();
            if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                if (json.carPosDTOs) {
                    setCarPoss(json.carPosDTOs);
                    setOnlineCarCount(json.carPosDTOs.length);
                    setOfflineCarCount(carCount - json.carPosDTOs.length);
                } else {
                    setCarPoss([]);
                    setOnlineCarCount(0);
                    setOfflineCarCount(carCount);
                }

                if (json.faultDTOs) {
                    setFaults(json.faultDTOs);
                    populateLRVStatus(json.faultDTOs);
                } else {
                    setFaults([]);
                    populateLRVStatus([]);
                }

                if (json.carFaultDTOs) {
                    setCarFaults(json.carFaultDTOs);
                } else {
                    setCarFaults([]);
                }
            } else {
                if (json && json.msg) {
                    setSnackbarSeverity('error');
                    setSnackbarMessage(json.msg);
                    setOpenSnackBar(true);
                }
            }
        } catch (err) {
            console.error(err);
            setSnackbarSeverity('error');
            setSnackbarMessage('Error. Please contact administrator.');
            setOpenSnackBar(true);
        }
    };

    const populateLRVStatus = (faultDTOList) => {
        let currentLevelOneFaultCount = 0;
        let currentLevelTwoFaultCount = 0;

        faultDTOList.map((fault) => {
            switch (fault.level) {
                case 1:
                    currentLevelOneFaultCount += 1;
                    break;
                case 2:
                    currentLevelTwoFaultCount += 1;
                    break;
                default:
            }
        });

        setFaultCount(faultDTOList.length);
        setLevelOneFaultCount(currentLevelOneFaultCount);
        setLevelTwoFaultCount(currentLevelTwoFaultCount);
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await restfulManager.getStations(sessionManager.getAccessToken());
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    setStations(json.stations);
                } else {
                    if (json && json.msg) {
                        setSnackbarSeverity('error');
                        setSnackbarMessage(json.msg);
                        setOpenSnackBar(true);
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }

            try {
                const res = await restfulManager.getRouteStations(sessionManager.getAccessToken());
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    setRouteStations(json.routeStations);
                } else {
                    setRouteStations([]);
                    if (json && json.msg) {
                        setSnackbarSeverity('error');
                        setSnackbarMessage(json.msg);
                        setOpenSnackBar(true);
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }

            try {
                const res = await restfulManager.getCars(sessionManager.getAccessToken());
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    setCarCount(json.cars.length);
                } else {
                    if (json && json.msg) {
                        setSnackbarSeverity('error');
                        setSnackbarMessage(json.msg);
                        setOpenSnackBar(true);
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    }, []);

    useInterval(() => {
        getLatestCarPoss(carCount);
    }, API_CALL_INTERVAL, [carCount]);

    return (
        <>
            <Box sx={{ flexGrow: 1, padding: '8px' }}>
                <Stack direction="column">
                    <Stack direction="row">
                        <Stack direction="column">
                            <Box sx={{ width: '250px', border: '2px solid ' + StyleConstants.borderColor, backgroundColor: StyleConstants.componentBgColor, color: StyleConstants.fontColor }}>
                                <Stack direction="column" sx={{ textAlign: 'center', alignItems: 'center' }}>
                                    <Typography sx={{ ...Styles.contentFont3 }}>
                                        LRV Status
                                    </Typography>

                                    <Stack direction="column" sx={{ width: '90%', border: '2px solid ' + StyleConstants.borderColor }}>
                                        <Typography sx={{ ...Styles.contentFont4, lineHeight: 1 }}>
                                            {onlineCarCount}
                                        </Typography>
                                        <Typography sx={{ ...Styles.contentFont3 }}>
                                            Operating
                                        </Typography>
                                    </Stack>

                                    <Stack direction="column" sx={{ width: '90%', border: '2px solid ' + StyleConstants.borderColor, marginTop: '8px' }}>
                                        <Typography sx={{ ...Styles.contentFont4, lineHeight: 1 }}>
                                            {offlineCarCount}
                                        </Typography>
                                        <Typography sx={{ ...Styles.contentFont3 }}>
                                            Offline
                                        </Typography>
                                    </Stack>

                                    <Stack direction="column" sx={{ width: '90%', border: '2px solid ' + StyleConstants.borderColor, marginTop: '8px' }}>
                                        <Typography sx={{ ...Styles.contentFont4, lineHeight: 1 }}>
                                            {faultCount}
                                        </Typography>
                                        <Typography sx={{ ...Styles.contentFont3 }}>
                                            Fault
                                        </Typography>
                                    </Stack>

                                    <Stack direction="column" sx={{ marginTop: '8px', width: '200px' }}>
                                        <Typography sx={{ ...Styles.contentFont2, borderBottom: '2px solid ' + StyleConstants.borderColor }}>
                                            Severity Level
                                        </Typography>
                                        <Stack direction="row" sx={{ width: '200px' }}>
                                            <Stack direction="column" sx={{ width: '100px' }}>
                                                <Typography sx={{ fontSize: '36px', color: StyleConstants.faultLevelOne, fontWeight: 900, lineHeight: 1 }}>
                                                    {levelOneFaultCount}
                                                </Typography>
                                                <Typography sx={{ ...Styles.contentFont2 }}>
                                                    Level I
                                                </Typography>
                                            </Stack>
                                            <Stack direction="column" sx={{ width: '100px' }}>
                                                <Typography sx={{ fontSize: '36px', color: StyleConstants.faultLevelTwo, fontWeight: 900, lineHeight: 1 }}>
                                                    {levelTwoFaultCount}
                                                </Typography>
                                                <Typography sx={{ ...Styles.contentFont2 }}>
                                                    Level II
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Box>
                            <Box sx={{ marginLeft: '16px' }}>
                                <LightStatusStack />
                            </Box>
                        </Stack>

                        <Box sx={{ width: '100%', textAlign: 'center' }}>
                            <RouteCanvas
                                left="360px"
                                top="64px"
                                routeCode={'map'}
                                stations={stations}
                                routeStations={routeStations}
                                carPoss={carPoss}
                                carFaults={carFaults}
                                />
                        </Box>
                    </Stack>
                    
                    <Stack direction="row">
                        <Box sx={{ width: '997px', height: '456px', border: '3px solid ' + StyleConstants.borderColor, backgroundColor: StyleConstants.componentBgColor, color: StyleConstants.fontColor, marginTop: '4px' }}>
                            <Stack direction="column" sx={{ height: '100%', paddingLeft: '8px', paddingRight: '8px', textAlign: 'center', alignItems: 'center' }}>
                                <Typography sx={{ width: '100%', ...Styles.contentFont4, textAlign: 'left' }}>
                                    Fault
                                </Typography>

                                <FaultTable isLoading={false} faults={faults} rowsPerPage={4} />
                            </Stack>
                        </Box>

                        <Box sx={{ width: '820px', height: '456px', border: '3px solid ' + StyleConstants.borderColor, backgroundColor: StyleConstants.componentBgColor, color: StyleConstants.fontColor, marginTop: '4px', marginLeft: '12px' }}>
                            <Stack direction="column" sx={{ height: '100%', paddingLeft: '8px', paddingRight: '8px', textAlign: 'center', alignItems: 'center' }}>
                                <Typography sx={{ width: '100%', ...Styles.contentFont4, textAlign: 'left' }}>
                                    LRV Status
                                </Typography>

                                <LrvStatusTable isLoading={false} carFaults={carFaults} rowsPerPage={10} />
                            </Stack>
                        </Box>
                    </Stack>
                </Stack>
            </Box>
        </>
    );
};

export default HomePage;